/**
 * ocg — Optional Content Groups (PDF layers) for the vector layout-plot PDF
 * (PDF-6.2b). Builds, per DISTINCT plotted drawing layer, one OCG dictionary and
 * the document-level `/OCProperties` catalog entry, and hands the renderer the
 * page-Resources `/Properties` keys + the `BDC … EMC` marked-content operators
 * that wrap each layer's geometry (see {@link layerSections}).
 *
 * # What an OCG is (PDF 32000-1:2008 §8.11)
 * An Optional Content Group is an indirect dict `<< /Type /OCG /Name (…) >>`.
 * Content is associated with it by wrapping it in a marked-content sequence
 *   `/OC /Pn BDC  …  EMC`
 * where `/Pn` names an entry in the page's Resources `/Properties` subdictionary
 * that resolves to the OCG (or an OCMD) reference. The document catalog carries
 * `/OCProperties << /OCGs [refs] /D << /ON [refs] /Order [refs] >> >>` listing
 * all groups and the default ON/Order configuration. Acrobat / Foxit render this
 * as a toggleable Layers panel; a simple viewer ignores `/OC` and paints all the
 * content (it is plain marked content to a non-OC reader).
 *
 * # Empty layers get NO OCG (lazy creation)
 * {@link DocumentOcgController.pageScope} → {@link PageOcgScope.markedContent}
 * creates the OCG for a layer the FIRST time that layer is wrapped. A layer that
 * is declared in the layer table but has no plotted entity (filtered out, or
 * simply unused) is never passed to `markedContent`, so it gets NO OCG and does
 * NOT appear in the Acrobat layer panel — matching AutoCAD's "Plot" behaviour
 * (an empty plotted layer contributes nothing).
 *
 * # One global OCG per layer — PDF has no per-VP layer state
 * AutoCAD lets a layer be frozen in one viewport but thawed in another
 * (per-VP VP-freeze, DXF group 70 bit / the VP's frozenLayers list). PDF
 * optional content has NO per-viewport layer state: an OCG is either ON or OFF
 * for the whole document configuration. So a layer is ONE global OCG regardless
 * of per-VP freeze. A layer frozen in VP-1 but visible in paper space / VP-2 is
 * still a single OCG, ON by default; its geometry that WAS visible (paper space,
 * VP-2) is wrapped in that OCG and its VP-1-frozen geometry was already filtered
 * out upstream (`isEntityVisibleInVp`) so it is simply absent — there is no
 * second "VP-1 copy" to suppress. This is an inherent PDF limitation, not a
 * simplification of the model: the model's per-VP freeze is fully honoured by
 * the upstream visibility filter; only the *toggle granularity in the PDF* is
 * per-document, because the format offers nothing finer.
 *
 * # Xref-namespaced layer names
 * If two xrefs each define a layer with the same local name they are DISTINCT
 * AutoCAD layers and must be DISTINCT OCGs. The convention is to namespace the
 * OCG /Name as `xref|layer`. The typed model the exporter consumes does NOT
 * expose an xref origin on a resolved layer name (layers arrive already merged
 * into one `styleTables.layers` table keyed by the plain — possibly already
 * `xref|layer`-prefixed — name the importer produced). We therefore key the OCG
 * by the plain stored layer name: collisions cannot occur in the table (a Map
 * keyed by name), and any xref prefixing the importer already applied rides
 * through verbatim. {@link OCG_XREF_NOTE} records this; true per-xref splitting
 * is a follow-up once the model carries layer provenance.
 */

import {
  PDFArray,
  PDFDict,
  PDFName,
  PDFOperator,
  PDFOperatorNames,
  PDFString,
  type PDFDocument,
  type PDFOperator as PDFOperatorType,
  type PDFPage,
  type PDFRef,
} from "pdf-lib";

/**
 * In-code note (CLAUDE.md PRIORITY #1: document a known model gap rather than
 * fake per-xref OCG splitting). The resolved layer table does not carry xref
 * provenance, so the OCG /Name is the plain stored layer name; any xref prefix
 * the importer applied (`xref|layer`) is preserved verbatim.
 */
export const OCG_XREF_NOTE =
  "PDF-6.2b: OCG keyed by plain stored layer name; xref provenance is not exposed by the typed model, so per-xref name splitting rides on whatever the importer already prefixed.";

/**
 * In-code note: PDF optional content has no per-viewport layer state, so a
 * layer that is VP-frozen in one viewport but visible elsewhere is ONE global
 * OCG (ON by default). The per-VP freeze is honoured upstream by the visibility
 * filter (frozen geometry is absent); only the PDF toggle is per-document.
 */
export const OCG_PER_VP_NOTE =
  "PDF-6.2b: one global OCG per layer (PDF has no per-VP optional-content state); per-VP freeze is applied upstream, the OCG toggle is document-wide.";

/** A resolved marked-content wrapper for one layer section. */
export interface OcgMarkedContent {
  /** `/OC /Pn BDC` — push BEFORE the section's paint operators. */
  begin: PDFOperatorType;
  /** `EMC` — push AFTER the section's paint operators. */
  end: PDFOperatorType;
}

/**
 * Per-page optional-content scope. Bound to ONE {@link PDFPage}; created via
 * {@link DocumentOcgController.pageScope}. Lazily creates the document-level OCG
 * for a layer the first time it is wrapped and assigns a stable page-Resources
 * `/Properties` key (`/OC0`, `/OC1`, … in first-seen order on this page).
 */
export interface PageOcgScope {
  /**
   * Resolve the `BDC … EMC` wrapper for a layer section. Lazily creates the
   * layer's global OCG (shared across pages) on first use and registers a
   * page-Resources `/Properties` key → that OCG ref. Returns the begin/end
   * operators the caller pushes around the section's paint operators.
   *
   * The same layer on the same page reuses ONE `/Properties` key (and one OCG);
   * the same layer on a DIFFERENT page reuses the same OCG but registers a fresh
   * per-page key (PDF property keys are page-Resources-scoped).
   */
  markedContent(layerName: string): OcgMarkedContent;
}

/**
 * Document-level optional-content controller. ONE per output {@link PDFDocument}
 * (OCGs and `/OCProperties` are catalog-level; a multi-page PUBLISH shares one
 * controller, each page's Resources maps the property keys IT uses). Drive it:
 *
 *   const ocg = createOcgController(pdf);
 *   // per page:
 *   const scope = ocg.pageScope(page);
 *   const mc = scope.markedContent("Walls");
 *   page.pushOperators(mc.begin, …section ops…, mc.end);
 *   // once, after all pages:
 *   ocg.finalize();
 */
export interface DocumentOcgController {
  /** Begin an optional-content scope for one page. */
  pageScope(page: PDFPage): PageOcgScope;
  /**
   * Write `/OCProperties` to the catalog with every created OCG (all ON, stable
   * first-seen order). A no-op when no layer was ever wrapped (no OCG created →
   * no `/OCProperties`, so a viewer sees an ordinary PDF). Idempotent.
   */
  finalize(): void;
  /** Number of distinct OCGs created so far (one per plotted layer). */
  ocgCount(): number;
}

/**
 * Create the per-document OCG controller. OCGs are created lazily as layers are
 * wrapped, so a document whose plotted content touches layers A, B, C ends with
 * exactly three OCGs (named A, B, C) regardless of how many layers the table
 * declared.
 */
export function createOcgController(pdf: PDFDocument): DocumentOcgController {
  const context = pdf.context;

  // layerName → its document-level OCG ref (created on first wrap). Insertion
  // order is the stable layer order used for `/OCGs`, `/D /ON` and `/D /Order`.
  const ocgByLayer = new Map<string, PDFRef>();
  let finalized = false;

  /** Lazily create (or fetch) the global OCG for a layer name. */
  const ocgRefForLayer = (layerName: string): PDFRef => {
    const existing = ocgByLayer.get(layerName);
    if (existing) return existing;
    // `<< /Type /OCG /Name (layerName) >>` — the layer's optional-content group.
    const dict = context.obj({
      Type: PDFName.of("OCG"),
      Name: PDFString.of(layerName),
    });
    const ref = context.register(dict);
    ocgByLayer.set(layerName, ref);
    return ref;
  };

  const pageScope = (page: PDFPage): PageOcgScope => {
    // Per-page state: the page-local layerName → property key map and the page
    // Resources `/Properties` subdictionary (lazily attached on the first wrap).
    // Property keys are `/OC0`, `/OC1`, … in first-seen order ON THIS PAGE
    // (page-scoped — a different page numbers its own keys from 0).
    const propKeyByLayer = new Map<string, string>();
    let propsDict: PDFDict | null = null;
    let nextKeyIndex = 0;

    const ensurePropertyKey = (layerName: string): string => {
      const existing = propKeyByLayer.get(layerName);
      if (existing) return existing;

      const ocgRef = ocgRefForLayer(layerName);
      const key = `OC${nextKeyIndex++}`;
      propKeyByLayer.set(layerName, key);

      // Attach (or reuse) the page Resources `/Properties` dict and map key→OCG.
      // The renderer always adds a FRESH page, so we attach a new Properties
      // dict on first use; we still reuse any pre-existing one through
      // Resources() to be structurally robust if a page ever arrives with one.
      if (propsDict === null) {
        // `Resources()` is typed as possibly-undefined; a freshly added page
        // always has one, but if absent we create + attach it so /Properties has
        // a home.
        let resources = page.node.Resources();
        if (!resources) {
          resources = context.obj({});
          page.node.set(PDFName.of("Resources"), resources);
        }
        const found = resources.lookupMaybe(PDFName.of("Properties"), PDFDict);
        if (found instanceof PDFDict) {
          propsDict = found;
        } else {
          propsDict = context.obj({});
          resources.set(PDFName.of("Properties"), propsDict);
        }
      }
      propsDict.set(PDFName.of(key), ocgRef);
      return key;
    };

    return {
      markedContent(layerName: string): OcgMarkedContent {
        const key = ensurePropertyKey(layerName);
        // `/OC /OCn BDC` — begin an optional-content marked-content sequence
        // tagged `/OC`, with the property name resolving (via page Resources
        // /Properties) to the layer's OCG. `EMC` closes it.
        const begin = PDFOperator.of(PDFOperatorNames.BeginMarkedContentSequence, [
          PDFName.of("OC"),
          PDFName.of(key),
        ]);
        const end = PDFOperator.of(PDFOperatorNames.EndMarkedContent);
        return { begin, end };
      },
    };
  };

  const finalize = (): void => {
    if (finalized) return;
    finalized = true;
    if (ocgByLayer.size === 0) return; // no plotted layers → no /OCProperties.

    const allRefs = [...ocgByLayer.values()];

    const ocgsArr = PDFArray.withContext(context);
    const onArr = PDFArray.withContext(context);
    const orderArr = PDFArray.withContext(context);
    for (const ref of allRefs) {
      ocgsArr.push(ref);
      onArr.push(ref); // all layers ON by default (AutoCAD plots every layer).
      orderArr.push(ref); // flat Order in stable first-seen layer order.
    }

    // /D — the default optional-content configuration dictionary.
    const config = context.obj({
      // A configuration SHOULD carry a /Name (Acrobat shows it). "Layers" is the
      // AutoCAD-PDF convention for the default config.
      Name: PDFString.of("Layers"),
      // /BaseState defaults to /ON, but we list /ON explicitly for clarity and
      // to be unambiguous to viewers that key off the explicit array.
      ON: onArr,
      Order: orderArr,
    });

    const ocProperties = context.obj({
      OCGs: ocgsArr,
      D: config,
    });
    pdf.catalog.set(PDFName.of("OCProperties"), ocProperties);
  };

  return {
    pageScope,
    finalize,
    ocgCount: () => ocgByLayer.size,
  };
}
